import { createTheme } from "@mui/material/styles";

export const bkdp = {
  deepNavy: "#0B2545",
  signalBlue: "#1B98E0",
  steelBlue: "#4A7FA5",
  white: "#FFFFFF",
  cloud: "#F4F6F9",
  greyLight: "#DDE3EC",
  slate: "#8899AA",
  textBody: "#3D5166",
  frost: "#C5CDD8",
  teal: "#2A9D8F",
  sage: "#4E8B6F",
  amber: "#D98A28",
  crimson: "#C84B4B",
  plum: "#7055A0"
} as const;

const fontSans =
  "'Inter', 'Helvetica Neue', 'Helvetica', Arial, sans-serif";

export const appTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: bkdp.deepNavy,
      dark: "#081B33",
      light: bkdp.steelBlue,
      contrastText: bkdp.white
    },
    secondary: {
      main: bkdp.signalBlue,
      dark: bkdp.steelBlue,
      light: "#5BB4E8",
      contrastText: bkdp.white
    },
    info: { main: bkdp.signalBlue, contrastText: bkdp.white },
    success: { main: bkdp.sage, contrastText: bkdp.white },
    warning: { main: bkdp.amber, contrastText: bkdp.white },
    error: { main: bkdp.crimson, contrastText: bkdp.white },
    background: {
      default: bkdp.cloud,
      paper: bkdp.white
    },
    text: {
      primary: bkdp.deepNavy,
      secondary: bkdp.textBody,
      disabled: bkdp.slate
    },
    divider: bkdp.greyLight
  },
  shape: {
    borderRadius: 6
  },
  typography: {
    fontFamily: fontSans,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontFamily: fontSans,
      fontWeight: 800,
      fontSize: 48,
      lineHeight: 1.1,
      letterSpacing: "4px",
      textTransform: "uppercase"
    },
    h2: {
      fontFamily: fontSans,
      fontWeight: 800,
      fontSize: 30,
      lineHeight: 1.1,
      letterSpacing: "4px",
      textTransform: "uppercase"
    },
    h3: {
      fontFamily: fontSans,
      fontWeight: 800,
      fontSize: 24,
      lineHeight: 1.15,
      letterSpacing: "3px",
      textTransform: "uppercase"
    },
    h4: {
      fontFamily: fontSans,
      fontWeight: 700,
      fontSize: 22,
      lineHeight: 1.25,
      letterSpacing: "0.5px"
    },
    h5: {
      fontFamily: fontSans,
      fontWeight: 700,
      fontSize: 18,
      lineHeight: 1.3,
      letterSpacing: "0.3px"
    },
    h6: {
      fontFamily: fontSans,
      fontWeight: 700,
      fontSize: 13,
      lineHeight: 1.35,
      letterSpacing: "2.5px",
      textTransform: "uppercase"
    },
    subtitle1: {
      fontFamily: fontSans,
      fontWeight: 600,
      fontSize: 15,
      letterSpacing: 0
    },
    subtitle2: {
      fontFamily: fontSans,
      fontWeight: 600,
      fontSize: 13,
      letterSpacing: 0
    },
    body1: {
      fontFamily: fontSans,
      fontWeight: 400,
      fontSize: 15,
      lineHeight: 1.75
    },
    body2: {
      fontFamily: fontSans,
      fontWeight: 400,
      fontSize: 13,
      lineHeight: 1.7
    },
    button: {
      fontFamily: fontSans,
      fontWeight: 700,
      fontSize: 13,
      letterSpacing: "1.5px",
      textTransform: "none"
    },
    overline: {
      fontFamily: fontSans,
      fontWeight: 700,
      fontSize: 10,
      letterSpacing: "3px",
      textTransform: "uppercase",
      lineHeight: 1.4
    },
    caption: {
      fontFamily: fontSans,
      fontWeight: 400,
      fontSize: 12,
      letterSpacing: 0,
      lineHeight: 1.55
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: bkdp.cloud,
          color: bkdp.deepNavy
        }
      }
    },
    MuiAppBar: {
      defaultProps: { elevation: 0, color: "default" },
      styleOverrides: {
        // HDR-1: the same surface as the drawer's paper, so header and drawer read as one chrome surface; the
        // bottom border is what separates chrome from content. Opaque: the bar is fixed and content scrolls under it.
        root: {
          backgroundColor: bkdp.white,
          color: bkdp.deepNavy,
          borderBottom: `1.5px solid ${bkdp.greyLight}`,
          boxShadow: "none"
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 72,
          "@media (min-width:600px)": { minHeight: 72 }
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: bkdp.white,
          borderRight: `1.5px solid ${bkdp.greyLight}`
        }
      }
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: "none"
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: bkdp.greyLight
        }
      }
    },
    MuiCard: {
      defaultProps: { elevation: 0, variant: "outlined" },
      styleOverrides: {
        root: {
          borderRadius: 10,
          borderWidth: 1.5,
          borderColor: bkdp.greyLight,
          backgroundColor: bkdp.white,
          transition:
            "border-color 150ms cubic-bezier(0.2, 0, 0.2, 1), box-shadow 150ms cubic-bezier(0.2, 0, 0.2, 1)"
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 28,
          "&:last-child": { paddingBottom: 28 }
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { paddingBottom: 0 },
        title: {
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "2.5px",
          textTransform: "uppercase",
          color: bkdp.deepNavy
        },
        subheader: {
          fontSize: 12,
          color: bkdp.slate,
          letterSpacing: 0
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: "11px 22px",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: "1.5px",
          textTransform: "none",
          borderWidth: 1.5,
          transition: "all 150ms cubic-bezier(0.2, 0, 0.2, 1)",
          "&:hover": { borderWidth: 1.5 },
          "&:active": { transform: "none", opacity: 0.9 }
        },
        contained: {
          backgroundColor: bkdp.deepNavy,
          color: bkdp.white,
          boxShadow: "none",
          "&:hover": {
            backgroundColor: bkdp.steelBlue,
            boxShadow: "none"
          }
        },
        containedSecondary: {
          backgroundColor: bkdp.signalBlue,
          "&:hover": { backgroundColor: bkdp.steelBlue }
        },
        outlined: {
          backgroundColor: bkdp.white,
          color: bkdp.deepNavy,
          borderColor: bkdp.greyLight,
          "&:hover": {
            color: bkdp.signalBlue,
            borderColor: bkdp.signalBlue,
            backgroundColor: bkdp.white
          }
        },
        text: {
          color: bkdp.signalBlue,
          padding: "8px 12px",
          letterSpacing: 0,
          fontWeight: 500,
          fontSize: 14,
          textTransform: "none",
          "&:hover": {
            backgroundColor: "transparent",
            color: bkdp.steelBlue
          }
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: bkdp.deepNavy,
          transition: "color 150ms cubic-bezier(0.2, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "transparent",
            color: bkdp.signalBlue
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          height: 22,
          fontFamily: fontSans,
          fontWeight: 700,
          fontSize: 10,
          letterSpacing: "2px",
          textTransform: "uppercase",
          paddingLeft: 4,
          paddingRight: 4
        },
        filled: {
          backgroundColor: bkdp.greyLight,
          color: bkdp.deepNavy
        },
        colorPrimary: {
          backgroundColor: bkdp.deepNavy,
          color: bkdp.white
        },
        colorSecondary: {
          backgroundColor: bkdp.signalBlue,
          color: bkdp.white
        },
        colorInfo: {
          backgroundColor: "rgba(27, 152, 224, 0.12)",
          color: bkdp.signalBlue
        },
        colorSuccess: {
          backgroundColor: "rgba(78, 139, 111, 0.14)",
          color: bkdp.sage
        },
        colorWarning: {
          backgroundColor: "rgba(217, 138, 40, 0.14)",
          color: bkdp.amber
        },
        colorError: {
          backgroundColor: "rgba(200, 75, 75, 0.12)",
          color: bkdp.crimson
        }
      }
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          backgroundColor: bkdp.white,
          fontSize: 14,
          "& fieldset": {
            borderWidth: 1.5,
            borderColor: bkdp.greyLight
          },
          "&:hover fieldset": { borderColor: bkdp.steelBlue },
          "&.Mui-focused fieldset": {
            borderColor: bkdp.signalBlue,
            borderWidth: 1.5
          }
        },
        input: {
          padding: "10px 14px"
        }
        /**
         * SHL-5: there is deliberately no `notchedOutline` override here, and the next person should not add one.
         * MUI computes the notch from the label itself: the legend's width comes from the label's measured text,
         * and its span carries MUI's own 5px either side. An override once padded the fieldset by 6px and the span by
         * 8px each side "to give the label air"; it did the opposite. On a labelled field the extra padding sat inside
         * a legend whose width was already fixed, so the word ran past the cut and the outline drew through it in the
         * focus colour. On a field with no label the legend is empty, so the same padding opened a 16px gap in the
         * top-left of the outline with nothing in it. If the label ever reads tight against the border, the lever is
         * the label's font size or MUI's own span padding, never padding a box whose width is computed elsewhere.
         */
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 13,
          color: bkdp.textBody,
          "&.Mui-focused": { color: bkdp.signalBlue }
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: bkdp.greyLight
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          marginInline: 8,
          paddingBlock: 8,
          color: bkdp.deepNavy,
          transition: "all 150ms cubic-bezier(0.2, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "transparent",
            color: bkdp.signalBlue
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(27, 152, 224, 0.08)",
            color: bkdp.signalBlue,
            "&:hover": { backgroundColor: "rgba(27, 152, 224, 0.12)" }
          }
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "3px",
          textTransform: "uppercase"
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: bkdp.greyLight,
          fontSize: 13,
          color: bkdp.textBody
        },
        head: {
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "3px",
          textTransform: "uppercase",
          color: bkdp.signalBlue,
          backgroundColor: bkdp.cloud
        }
      }
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          border: `1.5px solid ${bkdp.greyLight}`,
          borderRadius: 10,
          backgroundColor: bkdp.white
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: bkdp.signalBlue,
            "& + .MuiSwitch-track": {
              backgroundColor: bkdp.signalBlue,
              opacity: 0.5
            }
          }
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: bkdp.slate,
          "&.Mui-checked": { color: bkdp.signalBlue }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: bkdp.deepNavy,
          fontSize: 11,
          letterSpacing: "0.5px"
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1.5px solid ${bkdp.greyLight}`,
          borderRadius: 10,
          marginTop: 6
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: 13,
          letterSpacing: 0,
          "&.Mui-selected": {
            backgroundColor: "rgba(27, 152, 224, 0.08)"
          }
        }
      }
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          backgroundColor: bkdp.white,
          borderTop: `1.5px solid ${bkdp.greyLight}`
        }
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: bkdp.slate,
          "&.Mui-selected": { color: bkdp.signalBlue }
        },
        label: {
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "2px",
          textTransform: "uppercase",
          "&.Mui-selected": { fontSize: 10 }
        }
      }
    }
  }
});

export const theme = appTheme;
